<script setup lang="ts">
import { computed, toRefs } from 'vue';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const props = defineProps<{
  thumbnails: { [key: number]: string | null };
  imageClasses: { [key: number]: string };
  imageSizes: {
    [key: number]: { width: number; height: number; depth: number };
  };
}>();

const { thumbnails, imageClasses, imageSizes } = toRefs(props);

const sortedIndices = computed(() =>
  Object.keys(props.thumbnails)
    .map(Number)
    .sort((a, b) =>
      (props.imageClasses[a] || '').localeCompare(props.imageClasses[b] || ''),
    ),
);
</script>

<template>
  <div class="pa-4">
    <v-row class="equal-height-row">
      <v-col
        v-for="index in sortedIndices"
        :key="imageClasses[index] || index"
        cols="12"
        md="6"
        lg="4"
        xl="3"
      >
        <v-card class="mb-4 pa-4 d-flex flex-column">
          <h4 class="mb-2">
            {{ imageClasses[index] || $gettext('No class name') }}
          </h4>

          <div class="d-flex align-center justify-space-between">
            <!-- Cutout Image -->
            <div style="flex: 1">
              <img
                v-if="thumbnails[index]"
                :src="thumbnails[index] ?? undefined"
                :alt="`Cutout ${
                  imageClasses[index] || $gettext('No class name')
                }`"
                style="max-width: 100%; max-height: 200px; object-fit: contain"
              />
              <div v-else class="text-body-2 text-medium-emphasis">
                {{ $gettext('No cutout available') }}
              </div>
            </div>

            <!-- Size Info -->
            <div style="flex: 1; padding-left: 16px">
              <p class="text-caption mb-1">
                {{ $gettext('Size') }}
              </p>
              <div v-if="imageSizes[index]">
                <div>
                  {{ $gettext('Width') }}:
                  {{ imageSizes[index].width }}
                </div>
                <div>
                  {{ $gettext('Height') }}:
                  {{ imageSizes[index].height }}
                </div>
                <div>
                  {{ $gettext('Depth') }}:
                  {{ imageSizes[index].depth }}
                </div>
              </div>
              <div v-else class="text-body-2 text-medium-emphasis">
                {{ $gettext('No size specified') }}
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.equal-height-row .v-col {
  display: flex;
}

.equal-height-row .v-card {
  height: 100%;
}
</style>
